import React, { useState } from 'react'
import { css } from '@emotion/css'
import clsx from 'clsx'
import { ArrowRightIcon } from 'lucide-react'

import { DialogBoxSheet } from '~/design-system/atom/alert-dialog-sheet'
import { DialogHeader } from '~/design-system/atom/dialog'
import { Skeleton } from '~/design-system/atom/skeleton'
import { useToolkitInfo } from '~/ui/screens/appScreen/queries'

interface Step {
  title: string
}

export const WizardBox = ({
  children,
  open,
  setOpen,
  currentStep,
  title,
  logo,
  steps,
  appName,
}: {
  children: React.ReactNode
  open?: boolean
  setOpen: (e: boolean) => void
  currentStep: number
  title: string
  logo?: string
  subHeading?: string
  steps?: Step[]
  appName?: string
}) => {
  const { appInfo } = useToolkitInfo(appName ?? '')
  const [isLoaded, setIsLoaded] = useState(false)

  const logoSrc = logo ?? appInfo?.meta?.logo
  const logoAlt = logo ? title : (appName ?? '')
  const stepsArray = steps ?? [
    { title: 'Configure Integration' },
    { title: 'Connect an account' },
    { title: 'Execute tools' },
  ]

  return (
    <DialogBoxSheet
      open={open ?? true}
      isAnimating={true}
      setOpen={() => {
        setOpen?.(false)
      }}
      className={clsx(
        'flex h-[90vh] min-h-[92vh] min-w-[85vw] flex-col justify-start !space-y-0 !rounded-[40px] border-grey-600 bg-[#fff] px-0 py-0',
        css`
          gap: 0px;
        `,
      )}
    >
      <DialogHeader
        className="rounded-t-[40px] bg-[#fff] !px-10 py-5 !pt-6"
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        <div className="gap- flex items-center gap-[2px] font-cera text-[16px] font-[500] leading-none text-black-400">
          <div>
            {logoSrc && (
              <>
                <Skeleton
                  className={clsx(
                    'mr-2 h-7 w-7 rounded-[8px] border-[1px]',
                    isLoaded ? 'hidden' : 'block',
                  )}
                />
                <img
                  src={logoSrc}
                  alt={logoAlt}
                  className={clsx(
                    'mr-2 h-7 w-7 rounded-[8px] border-[1px]',
                    isLoaded && logoSrc ? 'block' : 'hidden',
                  )}
                  onLoad={() => setIsLoaded(true)}
                />
              </>
            )}
          </div>
          {title}
        </div>
        <div className="!mt-4 flex items-center gap-2 text-[13px] tracking-[.3px]">
          <div className="text-[13px] font-normal leading-none text-gray-800">
            Step {currentStep}/{stepsArray.length} :
          </div>
          {stepsArray.map((step, index) => (
            <React.Fragment key={index}>
              <div
                className={`font-normal leading-none ${currentStep === index + 1 ? 'text-gray-800' : 'text-gray-500'}`}
              >
                {step.title}
              </div>
              {index < stepsArray.length - 1 && (
                <ArrowRightIcon className="h-3 w-3 text-gray-500" />
              )}
            </React.Fragment>
          ))}
        </div>
      </DialogHeader>
      <div className="overflow-x-hidden overflow-y-scroll">{children}</div>
    </DialogBoxSheet>
  )
}
